import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useCurrency } from "@/context/CurrencyContext";
import CurrencySwitcher from "@/components/CurrencySwitcher";
import { 
  DollarSign, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  Loader2, 
  Building,
  RefreshCw,
  ArrowRight,
  Wallet,
  Receipt,
  AlertCircle,
  ExternalLink,
  CreditCard,
  Zap,
  Shield
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";

import { API_URL } from "@/utils/api";

export default function MyPayoutsPage({ user }) {
  const { formatAmount } = useCurrency();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [payoutInfo, setPayoutInfo] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Stripe Connect state
  const [connectStatus, setConnectStatus] = useState(null);
  const [loadingConnect, setLoadingConnect] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  useEffect(() => {
    fetchPayoutInfo();
    fetchConnectStatus();
    
    // Handle return from Stripe Connect onboarding
    const connectResult = searchParams.get("connect");
    if (connectResult === "success") {
      toast.success("Stripe Connect setup complete! Checking your account status...");
      fetchConnectStatus();
    } else if (connectResult === "refresh") {
      toast.info("Please complete your Stripe Connect setup");
    }
  }, []);

  const fetchPayoutInfo = async () => {
    setLoading(true);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/user/payout-info`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setPayoutInfo(data);
      }
    } catch (err) {
      console.error("Failed to fetch payout info:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchConnectStatus = async () => {
    setLoadingConnect(true);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/stripe-connect/status`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setConnectStatus(data);
      }
    } catch (err) {
      console.error("Failed to fetch connect status:", err);
    } finally {
      setLoadingConnect(false);
    }
  };

  const handleSetupStripeConnect = async () => {
    setOnboardingLoading(true);
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/stripe-connect/onboard`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          country: "US"
        })
      });
      const data = await res.json();
      if (res.ok && data.onboarding_url) {
        // Redirect to Stripe onboarding
        window.location.href = data.onboarding_url;
      } else if (data.status === "connected") {
        toast.success("Your payout account is already connected!");
        fetchConnectStatus();
      } else {
        toast.error(data.detail || "Failed to start setup");
      }
    } catch (err) {
      toast.error("Failed to start Stripe Connect setup");
    } finally {
      setOnboardingLoading(false);
    }
  };

  const handleOpenStripeDashboard = async () => {
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API_URL}/api/stripe-connect/dashboard`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = await res.json();
      if (res.ok && data.dashboard_url) {
        window.open(data.dashboard_url, "_blank");
      } else {
        toast.error(data.detail || "Could not open dashboard");
      }
    } catch (err) {
      toast.error("Failed to open Stripe dashboard");
    }
  };

  if (loading) {
    return (
      <DashboardLayout user={user} title="My Payouts">
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#121212]/40" />
        </div>
      </DashboardLayout>
    );
  }

  const stats = payoutInfo?.stats || {};
  const platformFee = payoutInfo?.platform_fee_percent || 1;
  const recentPayouts = payoutInfo?.recent_payouts || [];
  
  const isConnected = connectStatus?.connected && connectStatus?.payouts_enabled;
  const isPending = connectStatus?.connected && !connectStatus?.payouts_enabled;

  return (
    <DashboardLayout user={user} title="My Payouts">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#121212]">My Payouts</h1>
            <p className="text-sm text-[#121212]/60">Track your earnings and receive automated payouts</p>
          </div>
          <div className="flex items-center gap-2">
            <CurrencySwitcher />
            <Button variant="outline" size="sm" onClick={() => { fetchPayoutInfo(); fetchConnectStatus(); }} className="rounded-full">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* Stripe Connect Status Card */}
        {loadingConnect ? (
          <Card className="border-2 border-[#BFFF00]/30">
            <CardContent className="p-6 text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-500">Checking payout account status...</p>
            </CardContent>
          </Card>
        ) : isConnected ? (
          <Card className="border-2 border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-green-800">Automated Payouts Enabled</h3>
                    <Badge className="bg-green-200 text-green-800">Active</Badge>
                  </div>
                  <p className="text-sm text-green-700 mt-1">
                    Your earnings will be automatically transferred to your bank account.
                  </p>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="mt-3 border-green-300 text-green-700 hover:bg-green-100"
                    onClick={handleOpenStripeDashboard}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View Stripe Dashboard
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : isPending ? (
          <Card className="border-2 border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-amber-800">Complete Your Payout Setup</h3>
                    <Badge className="bg-amber-200 text-amber-800">Pending</Badge>
                  </div>
                  <p className="text-sm text-amber-700 mt-1">
                    Your payout account needs additional verification. Click below to complete the setup.
                  </p>
                  <Button 
                    size="sm" 
                    className="mt-3 bg-amber-600 hover:bg-amber-700"
                    onClick={handleSetupStripeConnect}
                    disabled={onboardingLoading}
                  >
                    {onboardingLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading...</>
                    ) : (
                      <><ArrowRight className="w-4 h-4 mr-2" /> Complete Verification</>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-2 border-[#BFFF00] bg-gradient-to-br from-[#F7FFE5] to-white">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-[#BFFF00] rounded-xl flex items-center justify-center">
                      <Zap className="w-6 h-6 text-[#121212]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-[#121212]">Enable Automated Payouts</h3>
                      <p className="text-sm text-[#121212]/60">Get paid automatically when clients pay</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                    <div className="flex items-center gap-2 text-sm text-[#121212]/70">
                      <Shield className="w-4 h-4 text-green-600" />
                      <span>Secure & verified</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#121212]/70">
                      <CreditCard className="w-4 h-4 text-blue-600" />
                      <span>Direct to your bank</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[#121212]/70">
                      <Zap className="w-4 h-4 text-amber-600" />
                      <span>Fast transfers</span>
                    </div>
                  </div>
                </div>
                <Button 
                  className="bg-[#121212] hover:bg-[#2a2a2a] rounded-full px-6"
                  onClick={handleSetupStripeConnect}
                  disabled={onboardingLoading}
                >
                  {onboardingLoading ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Setting up...</>
                  ) : (
                    <><ArrowRight className="w-4 h-4 mr-2" /> Set Up Payouts</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border border-green-200 bg-gradient-to-br from-green-50 to-white rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                <span className="text-xs text-green-600 font-medium">Total Collected</span>
              </div>
              <p className="text-2xl font-bold text-green-700">{formatAmount(stats.total_collected)}</p>
              <p className="text-xs text-green-600/70 mt-1">From paid payment links</p>
            </CardContent>
          </Card>

          <Card className="border border-amber-200 bg-gradient-to-br from-amber-50 to-white rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-amber-600" />
                <span className="text-xs text-amber-600 font-medium">Pending Payout</span>
              </div>
              <p className="text-2xl font-bold text-amber-700">{formatAmount(stats.pending_payout)}</p>
              <p className="text-xs text-amber-600/70 mt-1">Awaiting processing</p>
            </CardContent>
          </Card>

          <Card className="border border-blue-200 bg-gradient-to-br from-blue-50 to-white rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-blue-600" />
                <span className="text-xs text-blue-600 font-medium">Paid Out</span>
              </div>
              <p className="text-2xl font-bold text-blue-700">{formatAmount(stats.paid_out)}</p>
              <p className="text-xs text-blue-600/70 mt-1">Transferred to your account</p>
            </CardContent>
          </Card>

          <Card className="border border-[#BFFF00]/30 bg-gradient-to-br from-[#F7FFE5] to-white rounded-2xl">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Receipt className="w-5 h-5 text-[#121212]/60" />
                <span className="text-xs text-[#121212]/60 font-medium">Platform Fee</span>
              </div>
              <p className="text-2xl font-bold text-[#121212]">{platformFee}%</p>
              <p className="text-xs text-[#121212]/50 mt-1">{formatAmount(stats.platform_fees)} deducted</p>
            </CardContent>
          </Card>
        </div>

        {/* How It Works */}
        <Card className="border border-gray-200 rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Wallet className="w-5 h-5" />
              How Automated Payouts Work
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center p-4">
                <div className="w-10 h-10 bg-[#F7FFE5] rounded-full flex items-center justify-center mx-auto mb-2">
                  <span className="font-bold text-[#121212]">1</span>
                </div>
                <h4 className="font-medium text-sm">Create Payment Link</h4>
                <p className="text-xs text-gray-500 mt-1">Send link to your client</p>
              </div>
              <div className="text-center p-4">
                <div className="w-10 h-10 bg-[#F7FFE5] rounded-full flex items-center justify-center mx-auto mb-2">
                  <span className="font-bold text-[#121212]">2</span>
                </div>
                <h4 className="font-medium text-sm">Client Pays</h4>
                <p className="text-xs text-gray-500 mt-1">Payment collected via Stripe</p>
              </div>
              <div className="text-center p-4">
                <div className="w-10 h-10 bg-[#F7FFE5] rounded-full flex items-center justify-center mx-auto mb-2">
                  <span className="font-bold text-[#121212]">3</span>
                </div>
                <h4 className="font-medium text-sm">Auto Processing</h4>
                <p className="text-xs text-gray-500 mt-1">{platformFee}% platform fee deducted</p>
              </div>
              <div className="text-center p-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <h4 className="font-medium text-sm">Automatic Transfer</h4>
                <p className="text-xs text-gray-500 mt-1">Direct to your bank</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs: Overview, Payout History */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#F7FFE5] rounded-xl p-1">
            <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-white">
              Overview
            </TabsTrigger>
            <TabsTrigger value="history" className="rounded-lg data-[state=active]:bg-white">
              Payout History
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-4">
            <Card className="border border-gray-200 rounded-2xl">
              <CardHeader>
                <CardTitle className="text-base">Earnings Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Total Collected from Clients</span>
                    <span className="font-semibold">{formatAmount(stats.total_collected)}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Platform Fee ({platformFee}%)</span>
                    <span className="font-semibold text-red-600">- {formatAmount(stats.platform_fees)}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-gray-100">
                    <span className="text-gray-600">Already Paid Out</span>
                    <span className="font-semibold text-blue-600">- {formatAmount(stats.paid_out)}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 bg-amber-50 rounded-lg px-4">
                    <span className="font-medium text-amber-800">Pending Payout</span>
                    <span className="font-bold text-amber-700 text-xl">{formatAmount(stats.pending_payout)}</span>
                  </div>
                </div>
                
                {stats.pending_payout > 0 && !isConnected && (
                  <div className="mt-4 p-4 bg-[#F7FFE5] rounded-xl border border-[#BFFF00]/30">
                    <p className="text-sm text-[#121212]/70 text-center">
                      <Zap className="w-4 h-4 inline mr-1" />
                      Set up automated payouts to receive your pending balance automatically.
                    </p>
                    <Button 
                      size="sm" 
                      className="w-full mt-3 bg-[#121212] hover:bg-[#2a2a2a] rounded-full"
                      onClick={handleSetupStripeConnect}
                    >
                      Enable Automated Payouts
                    </Button>
                  </div>
                )}
                
                {stats.pending_payout > 0 && isConnected && (
                  <p className="text-sm text-gray-500 mt-4 text-center">
                    Payouts are processed automatically and transferred to your connected bank account.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payout History Tab */}
          <TabsContent value="history" className="mt-4">
            <Card className="border border-gray-200 rounded-2xl">
              <CardHeader>
                <CardTitle className="text-base">Payout History</CardTitle>
              </CardHeader>
              <CardContent>
                {recentPayouts.length === 0 ? (
                  <div className="text-center py-12">
                    <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No payouts yet</p>
                    <p className="text-sm text-gray-400 mt-1">Your payout history will appear here</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Transferred</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentPayouts.map((payout) => (
                          <tr key={payout.payout_id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 text-sm">
                              {payout.created_at ? new Date(payout.created_at).toLocaleDateString() : '-'}
                            </td>
                            <td className="py-3 px-4">
                              <span className="font-semibold text-green-600">{formatAmount(payout.payout_amount)}</span>
                            </td>
                            <td className="py-3 px-4">
                              <Badge className={
                                payout.status === "transferred" ? "bg-green-100 text-green-700" :
                                payout.status === "processed" ? "bg-blue-100 text-blue-700" :
                                "bg-gray-100 text-gray-700"
                              }>
                                {payout.status === "transferred" && <CheckCircle className="w-3 h-3 mr-1" />}
                                {payout.status}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-500">
                              {payout.transferred_at ? new Date(payout.transferred_at).toLocaleDateString() : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
